import { TENANT_SLUG_HEADER } from '@barber/shared';
import { hostIdentifiesTenant, resolveTenantSlug } from './tenant-slug';

/**
 * Cliente HTTP da API.
 *
 * ---
 *
 * ONDE MORA CADA TOKEN, E POR QUÊ
 *
 * O **access token** fica em MEMÓRIA. Não em `localStorage`: qualquer script
 * injetado na página leria de lá, e um token roubado vale enquanto durar.
 * Perdê-lo no reload é aceitável porque ele é recuperável — o que não é
 * recuperável não pode ficar exposto.
 *
 * O **refresh token** fica num cookie `httpOnly`, escrito pelo servidor. O
 * JavaScript nem o enxerga; o navegador o envia sozinho em `/auth/refresh`.
 * É por isso que `credentials: 'include'` está em toda requisição.
 *
 * No boot, o app chama `/auth/refresh` uma vez: se o cookie ainda valer, a
 * sessão volta sem o cliente digitar nada.
 */

const BASE_URL = '/api';

/**
 * O app manda o header de barbearia?
 *
 * O app do cliente e o painel da barbearia SEMPRE falam em nome de uma; o
 * painel master, NUNCA — ele é de quem não pertence a barbearia nenhuma, e um
 * `x-tenant-slug` esquecido no armazenamento local o faria pedir dados com um
 * tenant no contexto. O servidor recusaria, e o erro apareceria como "sem
 * permissão" numa tela que deveria funcionar.
 *
 * Por isso a escolha é explícita, feita uma vez no boot de cada app.
 */
export type TenantHeaderMode = 'auto' | 'never';

let tenantHeaderMode: TenantHeaderMode = 'auto';

export function configureApiClient(options: { tenantHeader: TenantHeaderMode }): void {
  tenantHeaderMode = options.tenantHeader;
}

/** Erro com o formato que a API devolve, para a UI decidir pelo CÓDIGO. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, string[]> | undefined,
    readonly payload?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Sem rede, servidor fora, DNS: nada chegou a ser processado. */
  get isOffline(): boolean {
    return this.status === 0;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Chamado quando a sessão morre de vez — a store limpa o usuário. */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<
    string,
    string | number | boolean | readonly (string | number)[] | undefined | null
  >;
  /** Rotas públicas e o próprio refresh não devem disparar renovação. */
  skipAuthRetry?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

  if (!query) return url;

  const params = new URLSearchParams();

  for (const [chave, valor] of Object.entries(query)) {
    if (valor === undefined || valor === null || valor === '') continue;
    // Lista vira o parâmetro repetido (`?serviceIds=a&serviceIds=b`), que é
    // como a API lê uma sequência de serviços.
    if (Array.isArray(valor)) {
      for (const item of valor) params.append(chave, String(item));
      continue;
    }
    params.set(chave, String(valor));
  }

  const texto = params.toString();
  return texto ? `${url}?${texto}` : url;
}

function buildHeaders(hasBody: boolean): Headers {
  const headers = new Headers();

  if (hasBody) headers.set('Content-Type', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  // Em produção o host identifica a barbearia e o servidor IGNORA este header.
  // Mandá-lo assim mesmo mantém um caminho só de código entre dev e produção.
  if (tenantHeaderMode === 'auto' && !hostIdentifiesTenant()) {
    const slug = resolveTenantSlug();
    if (slug) headers.set(TENANT_SLUG_HEADER, slug);
  }

  return headers;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;

  const tipo = response.headers.get('content-type') ?? '';

  if (tipo.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  const texto = await response.text();
  return texto === '' ? null : texto;
}

function toApiError(status: number, payload: unknown): ApiError {
  const corpo = (payload ?? {}) as {
    code?: string;
    message?: string | string[];
    details?: Record<string, string[]>;
  };

  const base = Array.isArray(corpo.message)
    ? corpo.message.join(' ')
    : (corpo.message ?? 'Não foi possível completar a operação.');

  return new ApiError(
    status,
    corpo.code ?? 'UNKNOWN',
    comDetalhes(base, corpo.details),
    corpo.details,
    payload,
  );
}

/**
 * Dobra os erros por campo DENTRO da mensagem.
 *
 * A API já manda `details` — `{ slug: ["Use apenas letras minúsculas..."] }` —
 * e a UI já os recebia em `ApiError.details`. Só que as trinta e quatro telas
 * que mostram um erro leem `error.message`, e nenhuma lê `details`. O resultado
 * é a pessoa vendo "Os dados enviados são inválidos" enquanto a resposta, ali
 * no mesmo objeto, diz exatamente qual campo e por quê.
 *
 * A junção acontece aqui, e não em cada tela, porque é o único ponto por onde
 * TODAS elas passam. Trocar trinta e quatro chamadas resolveria o mesmo, e a
 * próxima tela nasceria com o defeito de novo.
 *
 * O `_` é a chave que o servidor usa quando a issue não tem campo — aí não há
 * rótulo a mostrar, só a mensagem.
 */
function comDetalhes(mensagem: string, details?: Record<string, string[]>): string {
  if (!details) return mensagem;

  // Um rótulo por CAMPO, e não por mensagem: o mesmo campo costuma acumular
  // várias issues do Zod — "Telefone muito curto" e "Telefone inválido. Use DDD
  // e número" chegam juntas — e repetir o nome antes de cada uma polui sem
  // informar.
  const linhas = Object.entries(details)
    .map(([campo, mensagens]) => {
      const texto = (mensagens ?? []).filter(Boolean).join('; ');
      if (!texto) return '';
      return campo === '_' ? texto : `${campo}: ${texto}`;
    })
    .filter(Boolean)
    // Ponto no fim de cada campo: sem ele, o próximo rótulo cola na frase
    // anterior e os dois viram uma coisa só.
    .map((linha) => (/[.!?]$/.test(linha) ? linha : `${linha}.`));

  if (linhas.length === 0) return mensagem;

  // Ponto final antes da lista: sem ele, a mensagem genérica e o primeiro campo
  // viram uma frase só, e ninguém percebe onde uma termina e a outra começa.
  const prefixo = /[.!?]$/.test(mensagem) ? mensagem : `${mensagem}.`;
  return `${prefixo} ${linhas.join(' ')}`;
}

/**
 * Renovação em VOO ÚNICO.
 *
 * Cada refresh token funciona UMA vez, e reapresentar um já rotacionado é, para
 * o servidor, a assinatura de um token roubado: ele revoga a família inteira e
 * derruba a sessão de quem não fez nada de errado.
 *
 * Duas situações reais disparam chamadas concorrentes:
 *
 *   1. o app volta de segundo plano e várias consultas recebem 401 juntas;
 *   2. o `StrictMode` do React executa o efeito de abertura DUAS vezes em
 *      desenvolvimento — e foi assim que este bug apareceu: entrar, recarregar
 *      a página e ser expulso, com o log do servidor acusando "refresh token já
 *      revogado foi reapresentado".
 *
 * A trava resolve as duas: a segunda chamada espera o resultado da primeira em
 * vez de gastar o token de novo.
 */
export interface RefreshedSession {
  readonly accessToken: string;
  readonly user: { id: string; name: string; role: string };
}

let renovacaoEmVoo: Promise<RefreshedSession | null> | null = null;

/**
 * Renova a sessão pelo cookie. Devolve `null` quando não há sessão viva.
 *
 * **Todo caminho que renova passa por aqui** — o 401 no meio do uso e a
 * restauração na abertura do app. Se a abertura tivesse a própria chamada, as
 * duas poderiam correr juntas e cair na armadilha descrita acima.
 */
export async function refreshSession(): Promise<RefreshedSession | null> {
  renovacaoEmVoo ??= (async () => {
    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: buildHeaders(false),
        credentials: 'include',
      });

      if (!response.ok) return null;

      const corpo = (await parseBody(response)) as RefreshedSession | null;

      if (!corpo?.accessToken) return null;

      setAccessToken(corpo.accessToken);
      return corpo;
    } catch {
      return null;
    } finally {
      // Solta a trava no próximo tick: quem já esperava recebe o resultado
      // desta rodada, e uma renovação futura começa do zero.
      queueMicrotask(() => {
        renovacaoEmVoo = null;
      });
    }
  })();

  return renovacaoEmVoo;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, skipAuthRetry = false, signal } = options;

  const executar = async (): Promise<Response> =>
    fetch(buildUrl(path, query), {
      method,
      headers: buildHeaders(body !== undefined),
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });

  let response: Response;

  try {
    response = await executar();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;

    // Status 0: a requisição não chegou a lugar nenhum. A UI trata isso como
    // "sem conexão", que é uma mensagem acionável — diferente de "erro 500".
    throw new ApiError(0, 'NETWORK_ERROR', 'Sem conexão com o servidor. Verifique sua internet.');
  }

  if (response.status === 401 && !skipAuthRetry) {
    const renovou = await refreshSession();

    if (renovou) {
      response = await executar();
    } else {
      setAccessToken(null);
      onSessionLost?.();
    }
  }

  const payload = await parseBody(response);

  if (!response.ok) throw toApiError(response.status, payload);

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};
